// Before everything: the USM frame must not reach the storage it shares with a HOS tab.
import "./auth/embedFrame";
// Read first: ?hosShell=1 must be saved before an auth redirect drops the query string.
import "./components/hos/shell/hosShellFlag";
import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, HashRouter } from "react-router-dom";
import App from "./App";
import { AuthProvider } from "./auth/AuthContext";
import { PropertiesProvider } from "./properties/PropertiesContext";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import i18n from "./i18n";
import "./styles.css";
import "./theme.css";
// New HOS UI (DESIGN.md v2): the only global CSS it adds. Nothing here restyles legacy classes.
import "./styles/hos/hos-fonts.css";
import "./styles/hos/hos-tokens.css";
import { applyHosTheme } from "./styles/hos/hosTheme";
import { installStaleChunkReload } from "./utils/staleChunkReload";

// A tab opened before a deploy reloads once instead of showing "could not be displayed" for a missing chunk.
installStaleChunkReload();

const storedTheme = localStorage.getItem("xentros.theme");
document.documentElement.setAttribute("data-theme", storedTheme === "dark" ? "dark" : "light");
document.documentElement.style.colorScheme = storedTheme === "dark" ? "dark" : "light";
applyHosTheme(storedTheme === "dark" ? "dark" : "light");

// XEN-287 (E-31): <html lang> follows the interface language instead of staying
// "en" for a German or Polish workspace. A wrong lang makes a screen reader read
// German text with English phonetics and breaks hyphenation and spell checking.
// This only mirrors the language i18next has resolved — it changes no text and
// no language selection.
function syncDocumentLanguage() {
  const language = (i18n.resolvedLanguage ?? i18n.language ?? "en").split("-")[0];
  if (language && document.documentElement.lang !== language) {
    document.documentElement.lang = language;
  }
}
syncDocumentLanguage();
i18n.on("languageChanged", syncDocumentLanguage);

const isTauri = typeof window !== "undefined" && (window as { __TAURI__?: unknown }).__TAURI__;
const Router = isTauri ? HashRouter : BrowserRouter;

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <AppErrorBoundary
      title="Xentros could not start"
      description="A startup error was caught before the workspace could be displayed."
    >
      <AuthProvider>
        <PropertiesProvider>
          <Router>
            <App />
          </Router>
        </PropertiesProvider>
      </AuthProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);
